import React from 'react';
import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { SubscriptionTypeImages } from "../../../assets/subscriptions/imgconfig.ts";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import { GeoPoint, Timestamp } from "firebase/firestore"
import {
    Box,
    FormControlLabel,
    FormHelperText,
    FormLabel, IconButton,
    InputAdornment,
    MenuItem,
    Radio,
    RadioGroup,
    TextField,
    Checkbox,
    CircularProgress,
    Button,
    Tooltip
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import { RootState, useAppDispatch, useAppSelector } from "../../../state/state.tsx";
import { closeModal, toggleLoading } from "../../../state/ModalSlice/ModalSlice.ts";
import { useBackend } from "../../../hooks/backend/useBackend.ts";
import { getGeoHash, getLatLong } from "../utilityFunctions.ts";
import CloseIcon from "@mui/icons-material/Close";
import { UserStateV2 } from '../../Subscriptions/types/UserStateV2.ts';
import HelpOutlinedIcon from '@mui/icons-material/HelpOutlined';
import { toast } from 'react-toastify';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../Config/firebase.ts';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { SubscriptionType } from '../../../enums.ts';
import { selectBankAccounts, selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2.ts';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../Config/firebase.ts';


interface MultiMonthPrices {
    [key: string]: string;
}

interface MultiMonthPauseDays {
    [key: string]: string;
}

interface PlanDetails {
    name: string;
    price: string;
    validity: string;
    isCustom: boolean;
}

interface CountPricing {
    count: string;
    price: string;
    validity: string;
}

interface CountPricingError {
    count: boolean;
    price: boolean;
    validity: boolean;
}

interface PlanErrors {
    name: boolean;
    price: boolean;
    validity: boolean;
}


const GEO_LOC_API = import.meta.env.VITE_GEOLOCATION_API_KEY

type Binary = 0 | 1;

export default function AddSubscriptionModal() {

    // ------------------------DialogHandlers

    const Dispatch = useAppDispatch();
    const open = useAppSelector((state: RootState) => state.ModalSlice.AddSubOpen);
    const userSliceV2: UserStateV2 | null = useAppSelector((state: RootState) => state.userSliceV2?.data)
    const isLoading = useAppSelector((state: RootState) => state.ModalSlice.isLoading)


    const handleClose = () => {
        resetState()
        Dispatch(closeModal("AddSubOpen"))
    };


    // ------------------------Form States

    const [SelectedSubscriptionType, setSelectedSubscriptionType] = React.useState<string>("Preschool")
    const [Name, setName] = React.useState<string>("")
    // const [Description, setDescription] = React.useState<string>("")
    const [Price, setPrice] = React.useState<string>("")
    const [SubscriptionPaymentType, setSubscriptionPaymentType] = React.useState<string>("Monthly")
    const [SubscriptionNature, setSubscriptionNature] = React.useState<string>("Upfront")
    const [, setIsError] = React.useState<boolean>(false)
    const [location, setLocation] = useState<any>("");
    const [geoPoint, setGeoPoint] = React.useState<any>();
    const [paymentProfileID, setPaymentProfileID] = useState<number>(0)
    const [Advance, setAdvance] = React.useState<string>("")
    const [multiMonthPrices, setMultiMonthPrices] = React.useState<MultiMonthPrices>({
        FlexibleMonthly: "",
        Quarterly: "",
        Semester: "",
        Annual: "",
    })

    const [multiMonthPauseDays, setMultiMonthPauseDays] = React.useState<MultiMonthPauseDays>({
        FlexibleMonthly: "15",
        Quarterly: "31",
        Semester: "",
        Annual: ""
    })

    const [groupLink, setGroupLink] = React.useState<string>("");
    const [showWorkingDays, setShowWorkingDays] = useState(false);
    const [workingDays, setWorkingDays] = useState<[Binary, Binary, Binary, Binary, Binary, Binary, Binary]>([1, 1, 1, 1, 1, 1, 1]);
    const [mmAlign2monthBoundary, setmmAlign2monthBoundary] = useState<boolean>(false);
    const [NoGSTBatch, setNoGSTBatch] = useState<boolean>(false);
    const [isCountHourly, setIsCountHourly] = useState<boolean>(false);
    const [placeName, setPlaceName] = useState<string>("");
    const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    const handleDaySelection = (index: number) => {
        setWorkingDays(prev => {
            const newDays = [...prev] as [Binary, Binary, Binary, Binary, Binary, Binary, Binary];
            newDays[index] = prev[index] === 1 ? 0 : 1;
            return newDays;
        });
    };

    // Add new state for custom subscription image
    const [customSubscriptionImage, setCustomSubscriptionImage] = useState<{
        file: File | null;
        preview: string | null;
    }>({ file: null, preview: null });

    // Add handler for subscription image change
    const handleSubscriptionImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Check file size (5MB limit)
        if (file.size > 8 * 1024 * 1024) {
            toast.error('Image size should be less than 8MB');
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            setCustomSubscriptionImage({
                file,
                preview: reader.result as string
            });
        };
        reader.readAsDataURL(file);
    };

    // Add function to upload subscription image
    const handleSubscriptionImageUpload = async (): Promise<string | null> => {
        if (!customSubscriptionImage.file) return null;

        try {
            const timestamp = Date.now();
            const storageRef = ref(storage, `subscriptionImages/${ServiceProviderID}/${timestamp}_${customSubscriptionImage.file.name}`);
            await uploadBytes(storageRef, customSubscriptionImage.file);
            return await getDownloadURL(storageRef);
        } catch (error) {
            console.error("Error uploading subscription image:", error);
            toast.error('Failed to upload subscription image');
            return null;
        }
    };

    // Add new states for Count subscription type
    const [countPricings, setCountPricings] = useState<CountPricing[]>([{
        count: '',
        price: '',
        validity: '365'
    }]);

    const [countPricingErrors, setCountPricingErrors] = useState<CountPricingError[]>([{
        count: false,
        price: false,
        validity: false
    }]);

    // Handle count pricing changes
    const handleCountPricingChange = (index: number, field: keyof CountPricing, value: string) => {
        if (field === 'price' || field === 'count' || field === 'validity') {

            const regex = /^\d*\.?\d*$/;
            if (regex.test(value) || value === '') {
                const newPricings = [...countPricings];
                newPricings[index][field] = value;
                setCountPricings(newPricings);
            }
        } else {
            const newPricings = [...countPricings];
            newPricings[index] = { ...newPricings[index], [field]: value };
            setCountPricings(newPricings);
        }
    };

    // Add new count pricing row
    const handleAddCountPricing = () => {
        setCountPricings([...countPricings, {
            count: '',
            price: '',
            validity: '365'
        }]);
        setCountPricingErrors([...countPricingErrors, {
            count: false,
            price: false,
            validity: false
        }]);
    };

    // Remove count pricing row
    const handleRemoveCountPricing = (index: number) => {
        if (countPricings.length > 1) {
            setCountPricings(countPricings.filter((_, i) => i !== index));
            setCountPricingErrors(countPricingErrors.filter((_, i) => i !== index));
        }
    };

    // ----------------------- Reset States
    const resetState = () => {
        setSelectedSubscriptionType("Preschool");
        setName("");
        setPrice("");
        setSubscriptionPaymentType("Monthly");
        setSubscriptionNature("Upfront");
        setIsError(false);
        setLocation(null);
        setGeoPoint(undefined);
        setPaymentProfileID(0);
        setAdvance("");
        setMultiMonthPlans([]);
        setShowPlanSelector(false);
        setSelectedPlanType('');
        setGroupLink("");
        setModalError({
            Name: false,
            Price: false,
            Location: false,
            LocationError: "",
            MultiMonthPrices: {
                nullFilled: false,
                Quarterly: false,
                Semester: false,
                Annual: false,
                FlexibleMonthly: false
            }
        })
        setShowWorkingDays(false);
        setWorkingDays([0, 0, 0, 0, 0, 0, 0]);
        setCustomSubscriptionImage({ file: null, preview: null });
        setCountPricings([{ count: '', price: '', validity: '365' }]);
        setCountPricingErrors([{ count: false, price: false, validity: false }]);
        setIsCountHourly(false);
        setNoGSTBatch(false);
        setPlaceName("");
    };


    // ------------------------Handle Change Form States


    const handleChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
        setName(event.target.value)
    }

    const handleChangePrice = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        // -------------Function to handle only decimal and number inputs

        if (!value) {
            setPrice("")
            return;
        }

        let decimalCount = 0;
        for (let i = 0; i < value.length; i++) {

            const char = value[i];
            if (char >= '0' && char <= '9') { /* empty */
            } else if (char === '.') {
                if (decimalCount === 0) {
                    decimalCount++;
                } else {
                    return;
                }
            } else {
                return;
            }
        }

        setPrice(value)
    }

    const handleChangeAdvance = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        // -------------Function to handle only decimal and number inputs

        if (!value) {
            setAdvance("")
            return;
        }

        let decimalCount = 0;
        for (let i = 0; i < value.length; i++) {

            const char = value[i];
            if (char >= '0' && char <= '9') { /* empty */
            } else if (char === '.') {
                if (decimalCount === 0) {
                    decimalCount++;
                } else {
                    return;
                }
            } else {
                return;
            }
        }

        setAdvance(value)
    }


    const handleChangeSubscriptionPaymentType = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSubscriptionPaymentType(event.target.value)
    }

    const handleChangeSubscriptionNature = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSubscriptionNature(event.target.value)
    }

    const handleSubscriptionChange = (type: string) => {
        setSelectedSubscriptionType(type)
    }

    const handleChangePaymentProfile = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPaymentProfileID(Number(event.target.value))

    }

    const handleMultiMonthPriceChange = (period: keyof MultiMonthPrices) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;

        // Validate decimal input
        if (!value || /^\d*\.?\d*$/.test(value)) {
            setMultiMonthPrices(prev => ({
                ...prev,
                [period]: value
            }));
        }
    };

    const handlePauseDaysChange = (period: 'Quarterly' | 'Semester' | 'Annual' | 'FlexibleMonthly') => (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        // Only allow numbers
        if (!value || /^\d*$/.test(value)) {
            setMultiMonthPauseDays(prev => ({
                ...prev,
                [`${period}`]: value
            }));
        }
    };

    const handleGroupLinkChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setGroupLink(event.target.value);
    };
    const handleChangeMonthBoundary = (event: React.ChangeEvent<HTMLInputElement>) => {
        setmmAlign2monthBoundary(event.target.checked)
    }

    const handlePlaceNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPlaceName(event.target.value);
    };


    // ------------------------Error Handlers

    const [modalError, setModalError] = React.useState({
        Name: false, // Description: false,
        Price: false, Location: false,
        LocationError: "",
        MultiMonthPrices: {
            nullFilled: false,
            Quarterly: false,
            Semester: false,
            Annual: false,
            FlexibleMonthly: false
        }
    });

    // Add new state for plan errors
    const [planErrors, setPlanErrors] = useState<PlanErrors[]>([]);

    function CheckErrors(): boolean {
        setIsError(false)
        let localerror: boolean = false

        // Reset all error states
        setModalError({
            Name: false,
            Price: false,
            Location: false,
            LocationError: "",
            MultiMonthPrices: {
                nullFilled: false,
                Quarterly: false,
                Semester: false,
                Annual: false,
                FlexibleMonthly: false
            }
        });
        setPlanErrors([]);

        if (!location || !location.value) {
            setIsError(true)
            localerror = true
            setModalError((prev) => ({
                ...prev,
                Location: true,
                LocationError: "Location is required"
            }))
        }

        if (Name.trim() === "") {
            setIsError(true)
            localerror = true
            setModalError((prev) => ({
                ...prev,
                Name: true
            }))
        }

        if (SubscriptionPaymentType === "MultiMonth") {
            if (multiMonthPlans.length === 0) {
                localerror = true;
                setIsError(true);
                setModalError((prev) => ({
                    ...prev,
                    MultiMonthPrices: { ...prev.MultiMonthPrices, nullFilled: true }
                }));
            } else {
                // Check each plan for errors
                const newPlanErrors = multiMonthPlans.map(plan => ({
                    name: plan.isCustom && !plan.name.trim(),
                    price: !plan.price || Number(plan.price) < 6,
                    validity: !plan.validity || Number(plan.validity) <= 0
                }));

                // If any plan has errors, set the error flag
                if (newPlanErrors.some(error => error.name || error.price || error.validity)) {
                    localerror = true;
                    setIsError(true);
                }

                setPlanErrors(newPlanErrors);
            }
        } else if (SubscriptionPaymentType === "Monthly") {
            if (SelectedSubscriptionType !== "CableTV" && (!Price || Number(Price) < 6)) {
                localerror = true;
                setIsError(true);
                setModalError((prev) => ({
                    ...prev,
                    Price: true
                }));
            }
        } else if (SubscriptionPaymentType === "Count") {
            // Check if at least one count pricing exists
            if (countPricings.length === 0) {
                localerror = true;
                return localerror;
            }

            const newErrors = [...countPricingErrors];
            // Validate each count pricing entry
            countPricings.forEach((pricing, index) => {
                newErrors[index] = {
                    count: !pricing.count || Number(pricing.count) <= 0,
                    price: !pricing.price || Number(pricing.price) < 6,
                    validity: !pricing.validity || Number(pricing.validity) <= 0
                };

                if (newErrors[index].count || newErrors[index].price || newErrors[index].validity) {
                    localerror = true;
                }
            });
            setCountPricingErrors(newErrors);
        }

        return localerror
    }

    // --------------------------Form Options

    const [subscriptionTypes, setSubscriptionTypes] = useState<string[]>(['Preschool', 'Tuition', 'Sports', 'Fitness', 'Rentals', 'CableTV', 'Creche', 'Other',])

    //--------------------Listening for bank accounts for options
    const ServiceProviderID = useAppSelector(selectEffectiveUID)  
    const BankAccounts = useAppSelector(selectBankAccounts);
    // ------------------Listening for serviceTypes
    useEffect(() => {
        const fetchServiceTypes = async () => {
            if (ServiceProviderID) {
                const serviceProviderRef = doc(db, 'ServiceProviders', ServiceProviderID);
                const serviceProviderDoc = await getDoc(serviceProviderRef);
                
                if (serviceProviderDoc.exists()) {
                    const data = serviceProviderDoc.data();
                    if (data.ServiceTypes && data.ServiceTypes.length > 0) {
                        setSubscriptionTypes([...data.ServiceTypes]);
                        setSelectedSubscriptionType(data.ServiceTypes[0]);
                        setmmAlign2monthBoundary(data.mmAlign2monthBoundary || false);
                    } else {
                        setSubscriptionTypes(['Preschool', 'Tuition', 'Sports', 'Fitness', 'Rentals', 'CableTV', 'Creche', 'Other']);
                        setSelectedSubscriptionType('Preschool');
                    }
                }
            }
        };

        fetchServiceTypes();
    }, [ServiceProviderID, open]);

    useEffect(() => {
        if (location) {
            getLatLong(location.value.place_id).then((geometry: { lat: any, lng: any }) => {
                const geoPoint = new GeoPoint(geometry.lat, geometry.lng);

                setGeoPoint(geoPoint)
            })
            // Prepopulate the "Save as" field with location's main text
            setPlaceName(location.value.structured_formatting.main_text);
        }
    }, [location])

    // -------------------utilities
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    const selectedImage: string = SubscriptionTypeImages[SelectedSubscriptionType];

    // --------------------------On Submit Operation

    const { create } = useBackend(`/ServiceProviders/${ServiceProviderID}/Subscriptions`)

    async function onSubmit(e: any) {
        e.preventDefault()
        Dispatch(toggleLoading(true))
        try {
            const Localerrors = CheckErrors()

            if (Localerrors) {
                return
            }

            // Upload custom subscription image if present
            const subscriptionImageUrl = await handleSubscriptionImageUpload();

            // Convert multiMonthPlans to cyclePriceArray format
            const cyclePriceArray = SubscriptionPaymentType === "MultiMonth"
                ? multiMonthPlans.map(plan => ({
                    billingCycle: plan.isCustom ? plan.name : predefinedPlans.find(p => p.name === plan.name)?.value || plan.name,
                    price: Number(plan.price),
                    validity: Number(plan.validity),
                    pauseDays: 365
                }))
                : SubscriptionPaymentType === "Count"
                    ? countPricings.map(pricing => ({
                        billingCycle: isCountHourly ? SubscriptionType.Hours : SubscriptionType.Count,
                        count: Number(pricing.count),
                        price: Number(pricing.price),
                        validity: Number(pricing.validity)
                    }))
                    : [];

            const reqDoc = {
                BillingType: SubscriptionNature,
                CreatedAt: Timestamp.fromDate(new Date()),
                ModifiedAt: Timestamp.fromDate(new Date()),
                Name: Name,
                Price: SubscriptionPaymentType === "Monthly" ? Number(Price) : 0,
                cyclePriceArray,
                advance: Number(Advance),
                paymentProfileId: paymentProfileID,
                accountId: BankAccounts && BankAccounts[paymentProfileID] ? BankAccounts[paymentProfileID].razorpayAccountId : null,
                picture: subscriptionImageUrl || selectedImage,
                geoLocation: {
                    hash: getGeoHash(geoPoint),
                    latlang: geoPoint,
                },
                placeName: placeName,
                placeAddress: location.label,
                showMeInList: true,
                type: SelectedSubscriptionType,
                PaymentCycle: SubscriptionPaymentType,
                subscriptionType: SubscriptionPaymentType,
                groupLink: groupLink,
                days: workingDays,
                NoGSTBatch: NoGSTBatch,
                isCountHourly: isCountHourly,
                ...(SubscriptionPaymentType === "MultiMonth" && { align2monthlyBoundary: false })
            }

            await create(reqDoc)
            handleClose();
            resetState()
        } catch (error) {
            console.log(error)
        } finally {
            Dispatch(toggleLoading(false))
        }
    }

    // Create a component for Count subscription fields with consistent styling

    const [multiMonthPlans, setMultiMonthPlans] = useState<PlanDetails[]>([]);
    const [showPlanSelector, setShowPlanSelector] = useState(false);
    const [selectedPlanType, setSelectedPlanType] = useState<string>('');

    const predefinedPlans = [
        { name: 'Flexible Monthly', value: 'FlexibleMonthly' },
        { name: 'Quarterly', value: 'Quarterly' },
        { name: 'Semester', value: 'Semester' },
        { name: 'Custom Plan', value: 'Custom' }
    ];

    const handleAddPlan = () => {
        setShowPlanSelector(true);
    };

    const handlePlanTypeSelect = (planType: string) => {
        if (planType !== 'Custom' && multiMonthPlans.some(plan => !plan.isCustom && predefinedPlans.find(p => p.name === plan.name)?.value === planType)) {
            toast.error('This plan type is already added');
            return;
        }

        if (planType === 'Custom') {
            setMultiMonthPlans([...multiMonthPlans, {
                name: `Custom Plan ${multiMonthPlans.length + 1}`,
                price: '',
                validity: '',
                isCustom: true
            }]);
        } else {
            const planName = predefinedPlans.find(p => p.value === planType)?.name || planType;
            setMultiMonthPlans([...multiMonthPlans, {
                name: planName,
                price: '',
                validity: planType === 'Quarterly' ? '90' : planType === 'Semester' ? '180' : planType === 'Annual' ? '365' : '30',
                isCustom: false
            }]);
        }
        setShowPlanSelector(false);
    };

    const handlePlanChange = (index: number, field: keyof PlanDetails, value: string) => {
        const updatedPlans = [...multiMonthPlans];
        updatedPlans[index] = { ...updatedPlans[index], [field]: value };
        setMultiMonthPlans(updatedPlans);
    };

    const handleRemovePlan = (index: number) => {
        setMultiMonthPlans(multiMonthPlans.filter((_, i) => i !== index));
    };

    return (<React.Fragment>

        <Dialog
            sx={{
                padding: 0,
                '& .MuiPaper-root': {
                    borderRadius: '10px',
                },
            }}
            maxWidth="md"
            open={open}
            PaperProps={{
                component: 'div',

            }}
        >

            <div className={"absolute right-4 top-4"}><IconButton onClick={handleClose}>
                <CloseIcon />
            </IconButton></div>

            <DialogContent sx={{ paddingX: "60px", paddingY: "50px" }} className={"bg-white"}>
                <div className={"header pb-[20px] "}>
                    <p className={"text-cf_heading font-[600] "}>Create Batch</p>
                    <p className={"text-cf_small font-[400] "}>Add your plan details</p>
                </div>
                <div className={"p-0"}>
                    <div className={""}>
                        <div className={""}>

                            <div className={"w-[550px] flex flex-col items-center"}>
                                <div className="relative w-[300px] group">
                                    <img
                                        className="aspect-[4/3] w-full rounded-lg shadow-lg object-cover"
                                        src={customSubscriptionImage.preview || selectedImage}
                                        alt="Subscription"
                                    />

                                    {/* Overlay with upload button */}
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 rounded-lg flex items-center justify-center">
                                        <Button
                                            variant="outlined"
                                            component="label"
                                            sx={{
                                                opacity: 0,
                                                transform: 'translateY(10px)',
                                                transition: 'all 0.3s',
                                                '.group:hover &': {
                                                    opacity: 1,
                                                    transform: 'translateY(0)',
                                                },
                                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                borderRadius: '8px',
                                                textTransform: 'none',
                                                color: '#1D4ED8',
                                                borderColor: '#1D4ED8',
                                                '&:hover': {
                                                    backgroundColor: '#1D4ED8',
                                                    color: "#FFFFFF",
                                                    borderColor: '#1D4ED8',
                                                },
                                            }}
                                        >
                                            <span className="flex items-center gap-2">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                                Upload Image
                                            </span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                hidden
                                                onChange={handleSubscriptionImageChange}
                                            />
                                        </Button>
                                    </div>
                                </div>

                                {/* Image guidelines */}
                                <div className="mt-3 mb-6 text-center">
                                    <p className="text-sm text-gray-600 font-medium mb-1">Upload your subscription image</p>
                                    <p className="text-xs text-gray-500">Recommended size: 1600 x 1200px (Max 8MB)</p>
                                </div>
                            </div>
                            <div className={"SubscriptionChips w-[550px] mt-4 flex-wrap flex gap-2 justify-center"}>
                                {subscriptionTypes.map((type: string, index) => {
                                    return (<span key={index} onClick={() => handleSubscriptionChange(type)}
                                        className={`p-[8px] flex justify-center items-center font-thin cursor-pointer text-sm rounded-[8px]   ${type === SelectedSubscriptionType ? 'bg-cc_brand text-white border-red-500' : "bg-[#E8EDFB] text-cc_text-brand-tertiary border-gray-800"}`}>
                                        {type}
                                    </span>)
                                })}
                            </div>
                        </div>


                        <div className={"w-[550px]"}>
                            <Box
                                component={"div"}
                                // component="form"
                                sx={{}}


                            >
                                <div className={"TextFields flex flex-col gap-4 mt-5"}>
                                    <TextField
                                        onChange={handleChangeName}
                                        value={Name}
                                        size={"small"}
                                        required={true}
                                        id="Name"
                                        label="Name"
                                        error={modalError.Name}
                                        helperText={modalError.Name ? "Name is required" : ""}
                                        variant="outlined"
                                        fullWidth={true}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: 'grey', borderRadius: '10px'// Default border color
                                                }, '&:hover fieldset': {
                                                    borderColor: 'grey', // Border color on hover
                                                }, '&.Mui-focused fieldset': {
                                                    borderColor: 'skyblue', // Border color when focused
                                                }, '& .MuiInputBase-input': {
                                                    fontSize: '14px',
                                                },
                                            }, fontWeight: 400, fontSize: "1px",
                                        }}
                                    />



                                    <div className="mt-0">
                                        <FormLabel id="payment-cycle-group">Payment Cycle</FormLabel>
                                        <RadioGroup
                                            aria-labelledby="payment-cycle-group"
                                            name="payment-cycle"
                                            row
                                            value={SubscriptionPaymentType}
                                            onChange={handleChangeSubscriptionPaymentType}
                                        >
                                            <FormControlLabel
                                                value="Monthly"
                                                control={<Radio />}
                                                label="Monthly"
                                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '16px' } }}
                                            />
                                            <FormControlLabel
                                                value="MultiMonth"
                                                control={<Radio />}
                                                label="Multi-Month"
                                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '16px' } }}
                                            />
                                            <FormControlLabel
                                                value="Count"
                                                control={<Radio />}
                                                label="Count Based"
                                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '16px' } }}
                                            />
                                        </RadioGroup>
                                    </div>
                                    {SubscriptionPaymentType === "Monthly" && (
                                            <p className="text-sm  mb-4">Monthly plans start on the 1st of each month. To set a flexible start date, select the Multi-Month option*</p>
                                    )}

                                    {SubscriptionPaymentType === "Count" ? (
                                        <div className="space-y-1 mb-2">
                                            <p className="text-sm font-semibold mb-4">Define pricing plans</p>
                                            {countPricings.map((pricing, index) => (
                                                <div key={index} className="flex items-start gap-2 ">
                                                    <div className="flex flex-col" >
                                                        <TextField
                                                            value={pricing.count}
                                                            size="small"
                                                            label="Number of Classes"
                                                            onChange={(e) => handleCountPricingChange(index, 'count', e.target.value)}
                                                            error={countPricingErrors[index].count}
                                                            helperText={countPricingErrors[index].count ? "Must be > 0" : " "}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    '& fieldset': {
                                                                        borderColor: 'grey',
                                                                        borderRadius: '10px'
                                                                    },
                                                                    '&:hover fieldset': {
                                                                        borderColor: 'grey',
                                                                    },
                                                                    '&.Mui-focused fieldset': {
                                                                        borderColor: 'skyblue',
                                                                    },
                                                                    '& .MuiInputBase-input': {
                                                                        fontSize: '14px',
                                                                    },
                                                                },
                                                                fontWeight: 400,
                                                                width: '150px'
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="flex flex-col" >
                                                        <TextField
                                                            value={pricing.price}
                                                            size="small"
                                                            label="Price"
                                                            onChange={(e) => handleCountPricingChange(index, 'price', e.target.value)}
                                                            error={countPricingErrors[index].price}
                                                            helperText={countPricingErrors[index].price ? "Min ₹6 required" : " "}
                                                            InputProps={{
                                                                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                                            }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    '& fieldset': {
                                                                        borderColor: 'grey',
                                                                        borderRadius: '10px'
                                                                    },
                                                                    '&:hover fieldset': {
                                                                        borderColor: 'grey',
                                                                    },
                                                                    '&.Mui-focused fieldset': {
                                                                        borderColor: 'skyblue',
                                                                    },
                                                                    '& .MuiInputBase-input': {
                                                                        fontSize: '14px',
                                                                    },
                                                                },
                                                                fontWeight: 400,
                                                                width: '150px'
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="flex flex-col" >
                                                        <TextField
                                                            value={pricing.validity}
                                                            size="small"
                                                            label="Valid Days"
                                                            onChange={(e) => handleCountPricingChange(index, 'validity', e.target.value)}
                                                            error={countPricingErrors[index].validity}
                                                            helperText={countPricingErrors[index].validity ? "Must be > 0" : " "}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    '& fieldset': {
                                                                        borderColor: 'grey',
                                                                        borderRadius: '10px'
                                                                    },
                                                                    '&:hover fieldset': {
                                                                        borderColor: 'grey',
                                                                    },
                                                                    '&.Mui-focused fieldset': {
                                                                        borderColor: 'skyblue',
                                                                    },
                                                                    '& .MuiInputBase-input': {
                                                                        fontSize: '14px',
                                                                    },
                                                                },
                                                                fontWeight: 400,
                                                                width: '150px'
                                                            }}
                                                        />
                                                    </div>
                                                    <IconButton
                                                        onClick={() => handleRemoveCountPricing(index)}
                                                        disabled={countPricings.length === 1}
                                                        sx={{
                                                            color: 'grey',
                                                            '&:hover': {
                                                                color: 'red',
                                                            },
                                                            height: '40px',
                                                            alignSelf: 'flex-start'
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </div>
                                            ))}
                                            <Button
                                                startIcon={<AddIcon />}
                                                onClick={handleAddCountPricing}
                                                variant="outlined"
                                                size="small"
                                                sx={{
                                                    borderRadius: '8px',
                                                    textTransform: 'none',
                                                    color: '#1D4ED8',
                                                    borderColor: '#1D4ED8',
                                                    '&:hover': {
                                                        backgroundColor: '#1D4ED8',
                                                        color: '#FFFFFF',
                                                        borderColor: '#1D4ED8',
                                                    },
                                                }}
                                            >
                                                Add Price Plan
                                            </Button>
                                            
                                            <div className="flex items-center gap-2 mt-4">
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={isCountHourly}
                                                            onChange={(e) => setIsCountHourly(e.target.checked)}
                                                            sx={{
                                                                '&.Mui-checked': {
                                                                    color: '#1D4ED8',
                                                                },
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <div className="flex items-center gap-1">
                                                            <span className="text-sm">Track Hours</span>
                                                            <Tooltip 
                                                                title="Enable this to track attendance by hours instead of just present/absent" 
                                                                arrow
                                                                placement="top"
                                                            >
                                                                <HelpOutlinedIcon 
                                                                    fontSize="small" 
                                                                    className="text-gray-500 cursor-help"
                                                                    sx={{ fontSize: '18px' }}
                                                                />
                                                            </Tooltip>
                                                        </div>
                                                    }
                                                />
                                            </div>
                                        </div>
                                    ) : SubscriptionPaymentType === "MultiMonth" ? (
                                        <div className="space-y-4">
                                            <p className="text-sm font-semibold">Add subscription plans for different durations</p>

                                            <div className="space-y-3">
                                                {multiMonthPlans.map((plan, index) => (
                                                    <div
                                                        key={index}
                                                        className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50 hover:shadow-md transition-shadow"
                                                    >
                                                        <div className="flex-1 flex items-center gap-3 min-w-0">
                                                            {plan.isCustom ? (
                                                                <TextField
                                                                    value={plan.name}
                                                                    size="small"
                                                                    label="Plan Name"
                                                                    onChange={(e) => handlePlanChange(index, 'name', e.target.value)}
                                                                    variant="outlined"
                                                                    error={planErrors[index]?.name}
                                                                    helperText={planErrors[index]?.name ? "Plan name required" : ""}
                                                                    sx={{
                                                                        minWidth: '180px',
                                                                        maxWidth: '180px',
                                                                        '& .MuiOutlinedInput-root': {
                                                                            '& fieldset': { borderColor: 'grey', borderRadius: '10px' },
                                                                            '& .MuiInputBase-input': { fontSize: '14px' },
                                                                        }
                                                                    }}
                                                                />
                                                            ) : (
                                                                <div className="min-w-[180px] max-w-[180px] flex items-center h-[40px] px-3 bg-white border rounded-lg">
                                                                    <span className="text-gray-700 font-medium truncate">{plan.name}</span>
                                                                </div>
                                                            )}
                                                            <TextField
                                                                value={plan.validity}
                                                                size="small"
                                                                label="Duration (days)"
                                                                onChange={(e) => {
                                                                    const value = e.target.value;
                                                                    if (!value || /^\d*$/.test(value)) { // Only allow numbers
                                                                        handlePlanChange(index, 'validity', value);
                                                                    }
                                                                }}
                                                                variant="outlined"
                                                                error={planErrors[index]?.validity}
                                                                helperText={planErrors[index]?.validity ? "Duration required" : ""}
                                                                sx={{
                                                                    minWidth: '140px',
                                                                    maxWidth: '140px',
                                                                    '& .MuiOutlinedInput-root': {
                                                                        '& fieldset': { borderColor: 'grey', borderRadius: '10px' },
                                                                        '& .MuiInputBase-input': { fontSize: '14px' },
                                                                    }
                                                                }}
                                                            />

                                                            <TextField
                                                                value={plan.price}
                                                                size="small"
                                                                label="Price"
                                                                onChange={(e) => {
                                                                    handlePlanChange(index, 'price', e.target.value)
                                                                }}
                                                                variant="outlined"
                                                                error={planErrors[index]?.price}
                                                                helperText={planErrors[index]?.price ? "Min ₹6 required" : ""}
                                                                InputProps={{
                                                                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                                                }}
                                                                sx={{
                                                                    minWidth: '140px',
                                                                    maxWidth: '140px',
                                                                    '& .MuiOutlinedInput-root': {
                                                                        '& fieldset': { borderColor: 'grey', borderRadius: '10px' },
                                                                        '& .MuiInputBase-input': { fontSize: '14px' },
                                                                    }
                                                                }}
                                                            />

                                                        </div>
                                                        <div className="flex items-center pl-2">
                                                            <IconButton
                                                                onClick={() => handleRemovePlan(index)}
                                                                sx={{
                                                                    color: 'grey',
                                                                    '&:hover': { color: 'red' },
                                                                    padding: '8px'
                                                                }}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </div>
                                                    </div>
                                                ))}


                                                <div className="p-4 border rounded-lg bg-white">
                                                    <p className="mb-2 text-sm font-medium">Add Plan Type:</p>
                                                    <div className="grid grid-cols-3 gap-2">
                                                        {predefinedPlans.map((plan) => {
                                                            const isDisabled = plan.value !== 'Custom' &&
                                                                multiMonthPlans.some(p => !p.isCustom &&
                                                                    predefinedPlans.find(pp => pp.name === p.name)?.value === plan.value);

                                                            return (
                                                                <Button
                                                                    key={plan.value}
                                                                    onClick={() => handlePlanTypeSelect(plan.value)}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    disabled={isDisabled}
                                                                    sx={{
                                                                        textTransform: 'none',
                                                                        borderRadius: '8px',
                                                                        color: isDisabled ? 'grey' : '#1D4ED8',
                                                                        borderColor: isDisabled ? 'grey' : '#1D4ED8',
                                                                        '&:hover': {
                                                                            backgroundColor: isDisabled ? 'transparent' : '#1D4ED8',
                                                                            color: isDisabled ? 'grey' : '#FFFFFF',
                                                                            borderColor: isDisabled ? 'grey' : '#1D4ED8',
                                                                        },
                                                                    }}
                                                                >
                                                                    {plan.name}
                                                                </Button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                            </div>

                                            {modalError.MultiMonthPrices.nullFilled &&
                                                <p className="text-sm text-cc_error">Please add at least one plan</p>
                                            }
                                            {/* 
                                            TODO: Align to monthly bounday code commented, toggle it when needed.
                                            <div className="text-lg flex items-center mt-4">
                                                <FormControlLabel
                                                    label="Align Subscriptions to Month End?"
                                                    labelPlacement="start"
                                                    sx={{ marginLeft: '0' }}
                                                    control={
                                                        <Switch
                                                            checked={mmAlign2monthBoundary}
                                                            onChange={handleChangeMonthBoundary}
                                                            inputProps={{ 'aria-label': 'controlled' }}
                                                        />
                                                    }
                                                />
                                                <Tooltip title="When enabled, the subscription will end on the last day of their respective months (e.g. plan ending March 31, June 30, etc.) and subscription can be paused for a month at a time. NOTE: This setting cannot be changed after creating the subscription." arrow>
                                                    <HelpOutlinedIcon fontSize="small" className="ml-2 text-gray-500 cursor-help" />
                                                </Tooltip>
                                            </div> */}
                                        </div>
                                    ) : (
                                        <TextField
                                            value={Price}
                                            size="small"
                                            required
                                            id="Price"

                                            error={modalError.Price}
                                            helperText={modalError.Price ? "Price must be minimum ₹6" : ""}
                                            label={SelectedSubscriptionType === "Rentals" ? "Rent + Maintenance" : "Price"}
                                            onChange={handleChangePrice}
                                            variant="outlined"
                                            fullWidth
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">₹</InputAdornment>,

                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'grey', borderRadius: '10px'// Default border color
                                                    }, '&:hover fieldset': {
                                                        borderColor: 'grey', // Border color on hover
                                                    }, '&.Mui-focused fieldset': {
                                                        borderColor: 'skyblue', // Border color when focused
                                                    }, '& .MuiInputBase-input': {
                                                        fontSize: '14px', // Setting font size
                                                    },
                                                }, fontWeight: 400, fontSize: "1px",
                                            }}


                                        />
                                    )}

                                    <TextField
                                        value={Advance}
                                        size="small"
                                        id="Advance"
                                        label="Advance / Registration Fee"
                                        onChange={handleChangeAdvance}
                                        variant="outlined"
                                        fullWidth
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,

                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: 'grey', borderRadius: '10px'// Default border color
                                                }, '&:hover fieldset': {
                                                    borderColor: 'grey', // Border color on hover
                                                }, '&.Mui-focused fieldset': {
                                                    borderColor: 'skyblue', // Border color when focused
                                                }, '& .MuiInputBase-input': {
                                                    fontSize: '14px', // Setting font size
                                                },
                                            }, fontWeight: 400, fontSize: "1px",
                                        }}


                                    />

                                    <TextField
                                        size={"small"}
                                        id="Bank Account"
                                        select
                                        label="Bank account"
                                        defaultValue={0}
                                        value={paymentProfileID}
                                        onChange={handleChangePaymentProfile}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: 'grey', borderRadius: '10px'// Default border color
                                                }, '&:hover fieldset': {
                                                    borderColor: 'grey', // Border color on hover
                                                }, '&.Mui-focused fieldset': {
                                                    borderColor: 'skyblue', // Border color when focused
                                                }, '& .MuiInputBase-input': {
                                                    fontSize: '14px', // Setting font size
                                                },
                                            }, fontWeight: 400, fontSize: "1px",
                                        }}
                                    >
                                        {BankAccounts && BankAccounts.length > 0 ? (
                                            BankAccounts.map((bankAccount, index) => (
                                                <MenuItem key={index} sx={{ fontSize: '14px' }} value={index}>
                                                    {bankAccount.iFSC.slice(0, 4) + " - " + bankAccount.accountNo}
                                                </MenuItem>
                                            ))
                                        ) : (
                                            <MenuItem key="0" sx={{ fontSize: '14px' }} value={0}>
                                                No account Found
                                            </MenuItem>
                                        )}
                                    </TextField>



                                    {/*-----------------GoogleLocation*/}
                                    <div>
                                        <GooglePlacesAutocomplete
                                            apiKey={GEO_LOC_API || process.env.GEO_LOC_API}
                                            selectProps={{
                                                placeholder: (<span>  <SearchIcon /> Search Location</span>), // Change the placeholder text here
                                                styles: {
                                                    input: (provided) => ({
                                                        ...provided, backgroundColor: 'transparent', // Fully opaque white background
                                                    }), option: (provided) => ({
                                                        ...provided, backgroundColor: 'transparent', // Fully opaque white background
                                                    }), singleValue: (provided) => ({
                                                        ...provided, backgroundColor: 'transparent', // Fully opaque white background
                                                    }), control: (provided) => ({
                                                        ...provided,
                                                        backgroundColor: 'transparent', // Fully opaque white background
                                                        borderColor: modalError.Location ? "#D32F2F" : "gray",
                                                        boxShadow: 'none', // Remove the default shadow
                                                        '&:hover': {
                                                            borderColor: 'black', // Keep the border black on hover
                                                        },
                                                        '&:focus': {
                                                            borderColor: 'black', // Ensure the border is black when active
                                                        },
                                                        '&:active': {
                                                            borderColor: "black"
                                                        },
                                                        borderRadius: '10px',
                                                        fontSize: '14px'
                                                    }), menu: (provided) => ({
                                                        ...provided, backgroundColor: 'rgba(255, 255, 255, 1)', // Fully opaque white background
                                                        zIndex: 100, // Set z-index to 100
                                                    }), menuPortal: (provided) => ({
                                                        ...provided, zIndex: 100, // Ensure the menu portal has a z-index of 100
                                                    }),
                                                },


                                                onChange: setLocation, // Render the menu in a portal to ensure the z-index works
                                            }}
                                        />

                                        {modalError.Location && (
                                            <FormHelperText error={true} sx={{ marginLeft: "1rem" }}>
                                                {modalError.LocationError || "Location is required"}
                                            </FormHelperText>
                                        )}

                                    </div>

                                    {/* Save as field - only show when location is selected */}
                                    {location && (
                                        <TextField
                                            value={placeName}
                                            size="small"
                                            id="PlaceName"
                                            label="Save Location as"
                                            onChange={handlePlaceNameChange}
                                            variant="outlined"
                                            fullWidth
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'grey',
                                                        borderRadius: '10px'
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'grey',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'skyblue',
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '14px',
                                                    },
                                                },
                                                fontWeight: 400,
                                                fontSize: "1px",
                                            }}
                                        />
                                    )}
                                    <div className={"mt-0 "}>
                                        <FormLabel id="demo-controlled-radio-buttons-group"></FormLabel>

                                        <RadioGroup
                                            aria-labelledby="demo-controlled-radio-buttons-group"
                                            name="controlled-radio-buttons-group"
                                            row
                                            value={SubscriptionNature}
                                            onChange={handleChangeSubscriptionNature}

                                        >
                                            <FormControlLabel value="Upfront"
                                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '16px' } }}
                                                control={<Radio />}
                                                label="Upfront" />
                                            <FormControlLabel disabled value="EndOfCycle"
                                                sx={{
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '16px',
                                                        cursor: 'not-allowed'
                                                    }
                                                }}
                                                control={<Radio />}
                                                label="End Of Cycle" />
                                        </RadioGroup>
                                    </div>
                                    <TextField
                                        value={groupLink}
                                        size="small"
                                        id="GroupLink"
                                        label="Group Invite Link (Whatsapp)"
                                        onChange={handleGroupLinkChange}
                                        variant="outlined"
                                        fullWidth
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: 'grey',
                                                    borderRadius: '10px'
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'grey',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: 'skyblue',
                                                },
                                                '& .MuiInputBase-input': {
                                                    fontSize: '14px',
                                                },
                                            },
                                            fontWeight: 400,
                                            fontSize: "1px",
                                        }}
                                    />

                                    <div className="flex items-center gap-2 mt-2">
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={NoGSTBatch}
                                                    onChange={(e) => setNoGSTBatch(e.target.checked)}
                                                    sx={{
                                                        '&.Mui-checked': {
                                                            color: '#1D4ED8',
                                                        },
                                                    }}
                                                />
                                            }
                                            label={
                                                <div className="flex items-center gap-1">
                                                    <span className="">Exclude batch from GST</span>
                                                    <Tooltip 
                                                        title="This change will be valid only if GST is already configured" 
                                                        arrow
                                                        placement="top"
                                                    >
                                                        <HelpOutlinedIcon 
                                                            fontSize="small" 
                                                            className="text-gray-500 cursor-help"
                                                            sx={{ fontSize: '18px' }}
                                                        />
                                                    </Tooltip>
                                                </div>
                                            }
                                        />
                                    </div>

                                </div>
                            </Box>
                        </div>

                    </div>


                </div>
                <div className="mt-2">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={showWorkingDays}
                                onChange={(e) => setShowWorkingDays(e.target.checked)}
                                sx={{
                                    '&.Mui-checked': {
                                        color: '#1D4ED8',
                                    },
                                }}
                            />
                        }
                        label={<span className="">Specify Working Days</span>}
                    />

                    {showWorkingDays && (
                        <div className="my-3">
                            <FormLabel component="legend" sx={{ mb: 2 }}>
                                Select the days on which this service will be available:
                            </FormLabel>
                            <div className="flex flex-wrap gap-2">
                                {daysOfWeek.map((day, index) => (
                                    <button
                                        key={day}
                                        type="button"
                                        onClick={() => handleDaySelection(index)}
                                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors
                                            ${workingDays[index] === 1
                                                ? 'bg-cc_brand text-white'
                                                : 'bg-[#E8EDFB] text-cc_text-brand-tertiary hover:bg-gray-200'
                                            }`}
                                    >
                                        {day}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className={"flex gap-2 pt-4"}>
                    <button onClick={(e) => {
                        e.preventDefault();
                        handleClose()
                    }} disabled={isLoading}
                        className={`w-full bg-cc_bg-tertiary border border-solid border-gray-500 p-1.5 rounded-lg ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}>Cancel
                    </button>
                    <button
                        onClick={onSubmit} disabled={isLoading}
                        className={`w-full bg-cc_brand text-white p-1.5 rounded-lg border-gray-500 flex justify-center items-center ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}>{isLoading &&
                            <CircularProgress sx={{ color: "white" }} size={20} />}
                        <p className={"ml-2"}>{isLoading ? "Loading" : "Add Subscription"}</p>
                    </button>

                </div>
            </DialogContent>

        </Dialog>

    </React.Fragment>);
} 